import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle,
  CheckCircle,
  Clock,
  Download,
  Eye,
  FileText,
  Loader2,
  Tag,
  XCircle,
} from "lucide-react";

const API = process.env.REACT_APP_API_URL;

const Dato = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
    <div className="text-sm text-gray-800 truncate">{value || "—"}</div>
  </div>
);

const Aviso = ({ icon: Icon, color, titulo, texto }) => (
  <div className="text-center py-6">
    <Icon className={`w-14 h-14 mx-auto mb-3 ${color}`} />
    <h2 className="text-lg font-semibold text-gray-800">{titulo}</h2>
    <p className="text-sm text-gray-500 mt-1">{texto}</p>
  </div>
);

const CertificadoPublicoPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noEncontrado, setNoEncontrado] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/public/certificados/${token}`)
      .then((res) => setData(res.data))
      .catch(() => setNoEncontrado(true))
      .finally(() => setLoading(false));
  }, [token]);

  const pdfUrl = `${API}/public/certificados/${token}/pdf`;
  const empresa = data?.empresa;
  const equipo = data?.equipo;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 py-8 sm:py-14">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500 gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            <p className="text-sm">Consultando certificado…</p>
          </div>
        ) : noEncontrado ? (
          <div className="p-6">
            <Aviso
              icon={XCircle}
              color="text-red-500"
              titulo="Certificado no encontrado"
              texto="El código QR no corresponde a ningún certificado registrado."
            />
          </div>
        ) : (
          <>
            {/* Encabezado empresa */}
            <div className="flex items-center gap-4 px-6 py-5 border-b bg-gradient-to-r from-blue-50 to-white">
              {empresa?.logo ? (
                <img
                  src={empresa.logo}
                  alt={empresa.razon_social}
                  className="h-14 w-14 object-contain rounded-lg bg-white border p-1"
                />
              ) : (
                <div className="h-14 w-14 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                  <FileText size={24} />
                </div>
              )}
              <div className="min-w-0">
                <div className="font-semibold text-gray-900 leading-tight">
                  {empresa?.razon_social}
                </div>
                {empresa?.nit && (
                  <div className="text-xs text-gray-500">NIT {empresa.nit}</div>
                )}
                {(empresa?.telefono || empresa?.correo) && (
                  <div className="text-xs text-gray-500 truncate">
                    {[empresa.telefono, empresa.correo].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Tag size={16} className="text-blue-600" />
                {data.tipoDocumento === "certificado" ||
                data.estado !== "asignado" ||
                data.enRevision
                  ? "Certificado de calibración"
                  : "Informe de verificación"}{" "}
                N°
                <span className="font-mono font-semibold text-gray-900">
                  {data.numero}
                </span>
              </div>

              {data.estado === "libre" && (
                <Aviso
                  icon={AlertTriangle}
                  color="text-amber-500"
                  titulo="Sticker sin servicio asignado"
                  texto="Este sticker aún no ha sido asociado a una calibración. Si ya está pegado en un equipo, contacta a la empresa prestadora del servicio."
                />
              )}

              {data.estado === "anulado" && (
                <Aviso
                  icon={XCircle}
                  color="text-red-500"
                  titulo="Sticker anulado"
                  texto="Este sticker fue anulado y no respalda ningún certificado vigente."
                />
              )}

              {data.estado === "asignado" && data.enRevision && (
                <Aviso
                  icon={Clock}
                  color="text-amber-500"
                  titulo="Certificado en revisión"
                  texto="Este certificado está pendiente de la firma de revisión. Estará disponible cuando se complete."
                />
              )}

              {data.estado === "asignado" && !data.enRevision && (
                <>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl bg-gray-50 border p-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">
                        Fecha de calibración
                      </div>
                      <div className="text-xl font-bold text-gray-900">
                        {data.fechaCalibracion
                          ? new Date(data.fechaCalibracion).toLocaleDateString("es-CO", {
                              day: "2-digit",
                              month: "long",
                              year: "numeric",
                            })
                          : "—"}
                      </div>
                    </div>
                    {data.resultadoGlobal && data.resultadoGlobal !== "Pendiente" && (
                      <span
                        className={`inline-flex items-center gap-1.5 self-start sm:self-auto px-3 py-1 rounded-full text-sm font-medium border ${
                          data.resultadoGlobal === "Conforme"
                            ? "bg-green-50 text-green-700 border-green-200"
                            : data.resultadoGlobal === "No conforme"
                              ? "bg-red-50 text-red-700 border-red-200"
                              : "bg-amber-50 text-amber-700 border-amber-200"
                        }`}
                      >
                        {data.resultadoGlobal === "Conforme" ? (
                          <CheckCircle size={15} />
                        ) : (
                          <AlertTriangle size={15} />
                        )}
                        {data.resultadoGlobal}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    <Dato label="Equipo" value={equipo?.categoria} />
                    <Dato
                      label="Marca / modelo"
                      value={[equipo?.marca, equipo?.modelo].filter(Boolean).join(" · ")}
                    />
                    <Dato label="Serial" value={equipo?.serial} />
                    <Dato label="Código" value={equipo?.codigo} />
                    <Dato label="Cliente" value={equipo?.cliente} />
                    <Dato label="Sede" value={equipo?.sede} />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 pt-1">
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
                    >
                      <Eye size={16} /> Ver certificado
                    </a>
                    <a
                      href={`${pdfUrl}?descargar=1`}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold transition-colors"
                    >
                      <Download size={16} /> Descargar PDF
                    </a>
                  </div>
                  <p className="text-xs text-gray-400 text-center">
                    La generación del certificado puede tardar unos segundos.
                  </p>
                </>
              )}
            </div>
          </>
        )}
      </div>
      <img src="/logo.png" alt="Previonix" className="h-6 mt-6 opacity-60" />
    </div>
  );
};

export default CertificadoPublicoPage;
